import React, { useCallback, useEffect, useState } from "react";
import { adminAnimationApi } from "@/api/oraclePhase28Client";
import { useAnimationSettings, ANIMATION_DEFAULTS } from "@/components/animation/AnimationSettings";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Animation Settings Panel (Phase 30H).
 *
 * Read/write the org-level animation defaults from /api/adminone/animation/settings,
 * plus mirror the changes into the local AnimationSettingsContext so the operator
 * sees them live across the dashboard immediately.
 *
 * Settings:
 *   • enabled            — master switch
 *   • reduceMotion       — force reduced-motion mode
 *   • chyronSpeed        — 0.5x | 1x | 2x
 *   • voiceEnabled       — LOGIX-2 voice playback
 *   • dockEnabled        — LOGIX-2 dock
 *   • marketReplay       — market replay timeline
 *   • globalMap          — global markets live map
 *   • floatingCandles    — decorative candles
 *   • marketPulseHero    — hero band
 *   • dailyInsightCards  — insight cards
 *   • sectorHeatmap      — sector heatmap
 *   • signalFlow         — PERCEIVE→PLAN→ACT→SYNTHESIZE flow
 */

const TOGGLES = [
  { key: "enabled",           label: "Master animation switch" },
  { key: "reduceMotion",      label: "Force reduced motion (a11y)" },
  { key: "voiceEnabled",      label: "LOGIX-2 voice playback" },
  { key: "dockEnabled",       label: "LOGIX-2 bottom dock" },
  { key: "marketReplay",      label: "Market replay timeline" },
  { key: "globalMap",         label: "Global markets live map" },
  { key: "floatingCandles",   label: "Floating candle backdrop" },
  { key: "marketPulseHero",   label: "Market Pulse hero band" },
  { key: "dailyInsightCards", label: "Daily insight cards" },
  { key: "sectorHeatmap",     label: "Sector heatmap" },
  { key: "signalFlow",        label: "Signal flow animation" },
];

const CHYRON_SPEEDS = [
  { v: 0.5, label: "0.5×" },
  { v: 1.0, label: "1×" },
  { v: 1.5, label: "1.5×" },
  { v: 2.0, label: "2×" },
];

export default function AdminAnimationSettingsPanel({
  testid = "admin-animation-settings-panel",
} = {}) {
  const { prefs: localPrefs, setPrefs: setLocalPrefs } = useAnimationSettings();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const { data: payload } = await adminAnimationApi.get();
      setData(payload);
      // mirror into local context so the operator sees changes live
      if (payload?.settings) setLocalPrefs(payload.settings);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setBusy(false); }
  }, [setLocalPrefs]);

  useEffect(() => { load(); }, [load]);

  const current = data?.settings || localPrefs || ANIMATION_DEFAULTS;

  const update = async (next) => {
    setSaving(true); setErr(""); setOk("");
    try {
      const { data: payload } = await adminAnimationApi.set(next);
      setData(payload);
      if (payload?.settings) setLocalPrefs(payload.settings);
      setOk("Saved.");
      setTimeout(() => setOk(""), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "save_failed");
    } finally { setSaving(false); }
  };

  const onToggle = (key) => update({ [key]: !current[key] });
  const onSpeed = (v) => update({ chyronSpeed: v });
  const onReset = () => update({ ...ANIMATION_DEFAULTS });

  return (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />

      <header style={S.head}>
        <div>
          <span style={S.tagline}>Phase 30H · Animation Settings Mesh™</span>
          <h3 style={S.title}>Operator Motion Controls</h3>
        </div>
        <div style={S.headRight}>
          <button type="button" onClick={onReset} disabled={saving} style={S.resetBtn} data-testid={`${testid}-reset`}>
            ↺ Reset to defaults
          </button>
          <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>
            {busy ? "…" : "↻"}
          </button>
        </div>
      </header>

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}
      {ok && <div style={S.ok}  data-testid={`${testid}-ok`}>✓ {ok}</div>}

      <div style={S.grid}>
        {TOGGLES.map(({ key, label }) => {
          const v = Boolean(current[key]);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key)}
              disabled={saving}
              style={{
                ...S.toggle,
                borderColor: v ? "#10b981" : "rgba(197,160,89,0.40)",
                background:  v ? "rgba(16,185,129,0.10)" : "rgba(11,15,20,0.55)",
                color:       v ? "#10b981" : "#c5a059",
              }}
              data-testid={`${testid}-toggle-${key}`}
              aria-pressed={v}
            >
              <span style={S.toggleLbl}>{label}</span>
              <span style={S.togglePill}>{v ? "ON" : "OFF"}</span>
            </button>
          );
        })}
      </div>

      <div style={S.speedRow}>
        <span style={S.speedLbl}>Chyron speed</span>
        <div style={S.speedBtns}>
          {CHYRON_SPEEDS.map((sp) => {
            const active = Math.abs((current.chyronSpeed || 1) - sp.v) < 0.01;
            return (
              <button
                key={sp.v}
                type="button"
                onClick={() => onSpeed(sp.v)}
                disabled={saving}
                style={{
                  ...S.speedBtn,
                  borderColor: active ? "#fde68a" : "rgba(197,160,89,0.40)",
                  color:       active ? "#fde68a" : "#c5a059",
                  background:  active ? "rgba(252,211,77,0.10)" : "transparent",
                }}
                data-testid={`${testid}-speed-${sp.label}`}
                aria-pressed={active}
              >
                {sp.label}
              </button>
            );
          })}
        </div>
      </div>

      <footer style={S.foot}>
        <span style={S.note}>
          Settings are persisted to <code style={S.code}>oracle_animation_settings</code> and mirrored into each operator’s local prefs at <code style={S.code}>carelogix.animation.prefs</code>. Read-only public endpoint at <code style={S.code}>/api/oracle/animation/settings</code>.
        </span>
      </footer>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.92))", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', system-ui, sans-serif", maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  headRight: { display: "flex", alignItems: "center", gap: 8 },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  resetBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.40)", color: "#c5a059", padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  refresh:  { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.40)", color: "#c5a059", padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  ok:  { padding: "8px 12px", background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)", color: "#d1fae5", borderRadius: 10, fontSize: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 },
  toggle: { appearance: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px solid", borderRadius: 12, cursor: "pointer", fontFamily: "'Figtree', system-ui, sans-serif", textAlign: "left" },
  toggleLbl: { fontSize: 13 },
  togglePill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "2px 7px", border: "1px solid currentColor", borderRadius: 999 },
  speedRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: 10, background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, flexWrap: "wrap" },
  speedLbl: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.10em" },
  speedBtns: { display: "flex", gap: 4 },
  speedBtn: { appearance: "none", border: "1px solid", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  foot: {},
  note: { color: "#94a3b8", fontSize: 11.5, lineHeight: 1.4 },
  code: { background: "rgba(255,255,255,0.05)", padding: "1px 6px", borderRadius: 4, fontFamily: "'Space Mono', monospace", color: "#cbd5e1" },
};
